// Parallel HubSpot Forms API submission for the PDP's "Contact Us About
// This Product" form (#ProductContactForm, sections/main-product.liquid).
// Same pattern as assets/contact-form-hubspot.js. The <contact-form>
// custom element wrapping this markup is not defined anywhere in the
// theme's JS (confirmed by grep across assets/) -- an undefined custom
// element behaves as an inert wrapper, so this really is a plain native
// {% form 'contact' %} submission, not JS-intercepted like the
// "Email for Price" flow (assets/global.js's ProductEmailForm).
//
// Confirmed active render path: sections/main-product.liquid:465, the
// default product template's own section, used by every product page.
// Three alternate product templates also contain a literal duplicate of
// this same #ProductContactForm markup (templates/product.email-for-price.liquid,
// templates/product.lead-time.liquid, templates/product.gift-card.liquid)
// -- these are Shopify alternate templates that only apply if a merchant
// explicitly assigns one to a specific product, and none were confirmed
// actually assigned to any live product. Not wired to this script in
// this pass -- flagged as a possible follow-up if any of those templates
// turn out to be in active use, rather than assumed either way.
(function () {
  const HUBSPOT_PORTAL_ID = '342999960'; // same portal as every other HubSpot integration
  const HUBSPOT_FORM_GUID = 'f8687686-c36e-4f34-984f-d8dfce170edb'; // "Product Contact" HubSpot form

  const formEl = document.getElementById('ProductContactForm');
  if (!formEl) return;
  if (formEl.dataset.hubspotWired) return;
  formEl.dataset.hubspotWired = 'true';

  // This form has one combined "Name" field, but HubSpot's own UI is
  // built around the standard firstname/lastname properties -- same
  // rationale and same first-space split as contact-form-hubspot.js.
  function splitName(fullName) {
    const trimmed = (fullName || '').trim().replace(/\s+/g, ' ');
    if (!trimmed) return { firstName: '', lastName: '' };
    const spaceIndex = trimmed.indexOf(' ');
    if (spaceIndex === -1) return { firstName: trimmed, lastName: '' };
    return { firstName: trimmed.slice(0, spaceIndex), lastName: trimmed.slice(spaceIndex + 1) };
  }

  // Reuses requested_product_sku / requested_product_title (created for
  // the Email for Price integration) and contact_message (created for
  // Contact Us) rather than inventing new properties -- this form
  // represents the same underlying concept ("a customer asking about
  // product X") as those, just through a different entry point, so no
  // new HubSpot properties are needed for this integration, only a new
  // form mapping the ones that already exist.
  function buildHubSpotFields() {
    const fields = [];
    const push = (name, value) => {
      const v = (value ?? '').toString().trim();
      if (v) fields.push({ name, value: v });
    };
    const byId = (id) => (document.getElementById(id)?.value || '').toString().trim();
    const { firstName, lastName } = splitName(byId('ContactForm-name--product'));

    push('email', byId('ContactForm-email--product'));
    push('firstname', firstName);
    push('lastname', lastName);
    push('contact_message', byId('ContactForm-body--product'));
    push('requested_product_sku', byId('sku--product'));
    push('requested_product_title', byId('product_title--product'));
    push('inquiry_source', 'Product Contact');

    return fields;
  }

  // Same fetchClientIp() as every other HubSpot integration in this
  // project -- this endpoint doesn't capture the visitor's IP the way an
  // embedded HubSpot form does, so it has to be supplied manually.
  function fetchClientIp(timeoutMs = 1500) {
    return new Promise((resolve) => {
      const controller = new AbortController();
      const timer = setTimeout(() => { controller.abort(); resolve(null); }, timeoutMs);
      fetch('https://api.ipify.org?format=json', { signal: controller.signal, keepalive: true })
        .then((r) => r.json())
        .then((data) => { clearTimeout(timer); resolve(data && data.ip ? data.ip : null); })
        .catch(() => { clearTimeout(timer); resolve(null); });
    });
  }

  async function submitToHubSpot() {
    if (!HUBSPOT_PORTAL_ID || !HUBSPOT_FORM_GUID) {
      console.warn('HubSpot sync skipped: HUBSPOT_PORTAL_ID / HUBSPOT_FORM_GUID not configured yet.');
      return;
    }
    try {
      const hutkMatch = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/);
      const ip = await fetchClientIp();
      const payload = {
        fields: buildHubSpotFields(),
        context: {
          pageUri: window.location.href,
          pageName: document.title,
          ...(hutkMatch ? { hutk: hutkMatch[1] } : {}),
          ...(ip ? { ipAddress: ip } : {}),
        },
      };
      fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch((err) => console.warn('HubSpot submission failed (Shopify submission is unaffected):', err));
    } catch (err) {
      console.warn('HubSpot submission failed (Shopify submission is unaffected):', err);
    }
  }

  // Fire on submit, never block it. This form has no custom JS validation
  // beyond native HTML5 constraints, so by the time a 'submit' event
  // reaches here the browser has already accepted the input as valid.
  formEl.addEventListener('submit', () => {
    submitToHubSpot();
  });
})();
